<template>
  <div>
    <a-card :bordered="false" title="科目余额表（试算平衡）">
      <div class="table-page-search-wrapper">
        <a-form layout="inline" @keyup.enter.native="searchQuery">
          <a-row :gutter="24">
            <a-col :md="6" :sm="24"><a-form-item label="起始月份" :labelCol="labelCol" :wrapperCol="wrapperCol"><a-month-picker v-model="beginMoment" format="YYYY-MM" style="width:100%" /></a-form-item></a-col>
            <a-col :md="6" :sm="24"><a-form-item label="截止月份" :labelCol="labelCol" :wrapperCol="wrapperCol"><a-month-picker v-model="endMoment" format="YYYY-MM" style="width:100%" /></a-form-item></a-col>
            <a-col :md="6" :sm="24"><a-form-item label="门店" :labelCol="labelCol" :wrapperCol="wrapperCol"><store-tenant-select domain="finance" @change="handleStoreTenantChange"></store-tenant-select></a-form-item></a-col>
            <a-col :md="6" :sm="24">
              <span class="table-page-search-submitButtons">
                <a-button type="primary" @click="searchQuery">查询</a-button>
                <a-button style="margin-left: 8px" @click="searchReset">重置</a-button>
                <a-checkbox :checked="showQty" style="margin-left: 16px" @change="onQtyToggle">显示数量金额</a-checkbox>
              </span>
            </a-col>
          </a-row>
        </a-form>
      </div>
      <a-table bordered size="middle" rowKey="key" :columns="columns" :dataSource="tableData" :pagination="false" :loading="loading" :rowClassName="rowClassName" :scroll="tableScroll" />
      <div v-if="list.length" style="margin-top: 8px;">
        试算平衡：
        <span v-for="item in balanceItems" :key="item.label" :style="{ color: item.ok ? '#52c41a' : '#f5222d' }" style="margin-right: 12px;">{{ item.label }}借{{ item.ok ? '=' : '≠' }}贷</span>
      </div>
    </a-card>
  </div>
</template>

<script>
import moment from 'moment'
import { ledgerSubjectBalance, accountSetCurrent } from './api'
import StoreTenantSelect from '@/components/StoreTenantSelect'

const fmt2 = v => (v === null || v === undefined || v === '' || isNaN(Number(v))) ? '' : Number(v).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtQ = v => (v === null || v === undefined || v === '') ? '' : String(Number(v))
const AMOUNT_FIELDS = ['openingDebit', 'openingCredit', 'debitAmount', 'creditAmount', 'endingDebit', 'endingCredit']
const QTY_FIELDS = ['beginQty', 'debitQty', 'creditQty', 'balanceQty']

export default {
  name: 'SubjectBalanceList',
  components: { StoreTenantSelect },
  data() {
    return {
      labelCol: { span: 6 }, wrapperCol: { span: 17, offset: 1 },
      accountSetId: null, beginMoment: null, endMoment: null, loading: false,
      list: [], showQty: false,
      tableScroll: { x: 'max-content', y: 400 }
    }
  },
  computed: {
    columns() {
      const cols = [
        { title: '科目编码', dataIndex: 'subject_code', width: 120 },
        { title: '科目名称', dataIndex: 'subject_name' },
        { title: '期初余额', children: [
          { title: '借方', dataIndex: 'openingDebit', align: 'right', width: 110 },
          { title: '贷方', dataIndex: 'openingCredit', align: 'right', width: 110 }
        ] },
        { title: '本期发生额', children: [
          { title: '借方', dataIndex: 'debitAmount', align: 'right', width: 110 },
          { title: '贷方', dataIndex: 'creditAmount', align: 'right', width: 110 }
        ] },
        { title: '期末余额', children: [
          { title: '借方', dataIndex: 'endingDebit', align: 'right', width: 110 },
          { title: '贷方', dataIndex: 'endingCredit', align: 'right', width: 110 }
        ] }
      ]
      if (this.showQty) {
        cols.push({ title: '数量', children: [
          { title: '期初数量', dataIndex: 'beginQty', align: 'right', width: 100 },
          { title: '借方数量', dataIndex: 'debitQty', align: 'right', width: 100 },
          { title: '贷方数量', dataIndex: 'creditQty', align: 'right', width: 100 },
          { title: '余额数量', dataIndex: 'balanceQty', align: 'right', width: 100 }
        ] })
      }
      return cols
    },
    numTotals() {
      const t = {}
      AMOUNT_FIELDS.forEach((f) => { t[f] = this.list.reduce((a, r) => a + (Number(r[f]) || 0), 0) })
      QTY_FIELDS.forEach((f) => {
        let s = 0
        let has = false
        this.list.forEach((r) => {
          const v = r[f]
          if (v !== null && v !== undefined && v !== '') { s += Number(v) || 0; has = true }
        })
        t[f] = has ? Math.round(s * 1e6) / 1e6 : null
      })
      return t
    },
    tableData() {
      const rows = this.list.map((r, i) => {
        const row = { key: r.id || ('r' + i), subject_code: r.subject_code, subject_name: r.subject_name }
        AMOUNT_FIELDS.forEach((f) => { row[f] = fmt2(r[f]) })
        QTY_FIELDS.forEach((f) => { row[f] = fmtQ(r[f]) })
        return row
      })
      if (rows.length) {
        const t = { key: '__total__', subject_code: '', subject_name: '合计', isTotal: true }
        AMOUNT_FIELDS.forEach((f) => { t[f] = fmt2(this.numTotals[f]) })
        QTY_FIELDS.forEach((f) => { t[f] = this.numTotals[f] === null ? '' : fmtQ(this.numTotals[f]) })
        rows.push(t)
      }
      return rows
    },
    balanceItems() {
      const t = this.numTotals
      const ok = (a, b) => Math.abs(a - b) < 0.005
      return [
        { label: '期初', ok: ok(t.openingDebit, t.openingCredit) },
        { label: '本期', ok: ok(t.debitAmount, t.creditAmount) },
        { label: '期末', ok: ok(t.endingDebit, t.endingCredit) }
      ]
    }
  },
  created() {
    this.beginMoment = moment()
    this.endMoment = moment()
    this.loadCurrent()
  },
  mounted() {
    this.$nextTick(() => {
      const el = this.$el.querySelector('.ant-table')
      if (el) {
        const top = el.getBoundingClientRect().top
        this.tableScroll.y = Math.max(300, window.innerHeight - top - 24)
      }
    })
  },
  methods: {
    loadCurrent() {
      accountSetCurrent({}).then((res) => {
        if (res && res.success && res.data && res.data.id) { this.accountSetId = res.data.id; this.searchQuery() } else { this.$message.warning('尚未建账') }
      })
    },
    searchQuery() {
      this.loading = true
      const beginPeriod = this.beginMoment ? this.beginMoment.format('YYYY-MM') : null
      const endPeriod = this.endMoment ? this.endMoment.format('YYYY-MM') : null
      ledgerSubjectBalance({ accountSetId: this.accountSetId, beginPeriod: beginPeriod, endPeriod: endPeriod }).then((res) => {
        if (res && res.success) this.list = res.data || []
        else if (res && res.message) this.$message.warning(res.message)
        this.loading = false
      }).catch(() => { this.loading = false })
    },
    searchReset() { this.beginMoment = moment(); this.endMoment = moment(); this.searchQuery() },
    handleStoreTenantChange() { this.searchQuery() },
    onQtyToggle(e) { this.showQty = e.target.checked },
    rowClassName(record) { return record.isTotal ? 'gl-balance-total-row' : '' }
  }
}
</script>

<style scoped>
/deep/ .gl-balance-total-row td { background: #fafafa !important; font-weight: 600; }
</style>
